import React, { useEffect, useState, useRef } from 'react'
import { useParams, useLocation, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import httpClient from '../../httpClient'
import { avaSaveInfoAction, avaInterviewSelectors } from '../../redux'
import { AvaInterviewStatus } from '../../api'
import * as interviewSelectors from '../../interview/selectors'

const MagicLinkHandler = () => {
  const { interviewToken } = useParams<{ interviewToken: string }>()
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const agency = useSelector(interviewSelectors.agency)
  const interview = useSelector(avaInterviewSelectors.data)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authenticated, setAuthenticated] = useState(false)
  const hasDispatchedRef = useRef(false)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const token = searchParams.get('token')

    if (!token || !interviewToken) {
      setError('Invalid link')
      setLoading(false)
      return
    }

    // Wait for agency to load (AvaHOC requirement)
    if (agency?.id === '') {
      return // Still loading
    }

    if (!agency?.enable_ava) {
      setError('AVA is not enabled for this agency')
      setLoading(false)
      return
    }

    // Only authenticate once
    if (authenticated || hasDispatchedRef.current) {
      return
    }

    const authenticate = async () => {
      try {
        const response = await httpClient.get(`/ava/magic_link/authenticate`, {
          params: {
            token,
            interview_token: interviewToken,
          },
        })

        // Store JWT token
        if (response.data.token) {
          localStorage.setItem('token', response.data.token)
          httpClient.defaults.headers.Authorization = `Bearer ${response.data.token}`
        }

        setAuthenticated(true)
        hasDispatchedRef.current = true

        // Transition interview to interview step (this will update the interview status to IN_INTERVIEW)
        dispatch(
          avaSaveInfoAction({
            interviewToken,
            params: { to_interview: true },
          })
        )
      } catch (err) {
        const errorObj = err as any
        const errorMessage =
          errorObj.response?.data?.error || errorObj.message || 'Failed to authenticate'
        setError(errorMessage)
        setLoading(false)
      }
    }

    authenticate()
  }, [interviewToken, location, dispatch, agency, authenticated])
  const isInReview = interview?.status === AvaInterviewStatus.IN_INTERVIEW;

  // Wait for interview status to be updated to IN_INTERVIEW before redirecting
  useEffect(() => {
    if (authenticated && isInReview) {
      // Small delay to ensure state is fully updated
      setTimeout(() => {
        history.push(`/ava/${interviewToken}`)
      }, 100)
    }
  }, [authenticated, isInReview, history, interviewToken])

  if (loading) {
    return <div>Authenticating...</div>
  }

  if (error) {
    return <div>Error: {error}</div>
  }

  return null
}

export default MagicLinkHandler
